import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Trophy,
  Sparkles,
  Clock,
  Move,
  RotateCcw,
  ZoomIn,
  X,
  ArrowRight,
  Info,
  Star,
} from 'lucide-react';
import confetti from 'canvas-confetti';

interface PuzzleGameProps {
  onClose: () => void;
  onOpenLeaderboard: () => void;
}

interface SlotConfig {
  id: number;
  x: number; // percentage (0-100)
  y: number; // percentage (0-100)
  w: number; // percentage width
  h: number; // percentage height
  shape: number; // 0 to 5
  label: string;
}

interface DecoyConfig {
  image: string;
  width: number;
  height: number;
  slot: { x: number; y: number; w: number; h: number };
  shape: number;
  label: string;
}

interface PuzzleStage {
  id: number;
  name: string;
  title: string;
  author: string;
  year: string;
  material: string;
  status: string;
  image: string;
  width: number;
  height: number;
  slots: SlotConfig[];
  decoys: DecoyConfig[];
  description: string;
}

interface PieceItem {
  id: string; // e.g. 'p-0', 'd-0'
  targetSlotIndex: number; // 0..N or -1 for decoy
  isDecoy: boolean;
  image: string;
  stageWidth: number;
  stageHeight: number;
  slot: { x: number; y: number; w: number; h: number };
  shape: number;
  label?: string;
}

const STAGES: PuzzleStage[] = [
  {
    id: 0,
    name: 'Màn 1',
    title: 'Em Thúy',
    author: 'Trần Văn Cẩn',
    year: '1943',
    material: 'Sơn dầu',
    status: 'Bảo vật Quốc gia (2013)',
    image: '/images/artifacts/em-thuy-tran-van-can.webp',
    width: 1655,
    height: 2196,
    slots: [
      { id: 0, x: 8, y: 8, w: 17, h: 13, shape: 0, label: 'Góc tường phong nền sơn dầu' },
      { id: 1, x: 42, y: 6, w: 18, h: 13, shape: 1, label: 'Mái tóc búp bê cài hoa' },
      { id: 2, x: 74, y: 18, w: 17, h: 14, shape: 2, label: 'Tựa lưng ghế mây uốn lượn' },
      { id: 3, x: 36, y: 24, w: 18, h: 13, shape: 3, label: 'Gương mặt & ánh mắt thơ ngây' },
      { id: 4, x: 10, y: 38, w: 17, h: 14, shape: 4, label: 'Tay vịn mây đan bên trái' },
      { id: 5, x: 46, y: 44, w: 18, h: 13, shape: 5, label: 'Cổ áo lá sen trắng tinh khôi' },
      { id: 6, x: 72, y: 56, w: 18, h: 14, shape: 0, label: 'Khung ghế mây đan bên phải' },
      { id: 7, x: 20, y: 70, w: 19, h: 15, shape: 1, label: 'Chân ghế mây & quai guốc gỗ' },
    ],
    decoys: [
      {
        image: '/images/artifacts/giong-nguyen-tu-nghiem.webp',
        width: 2560,
        height: 1902,
        slot: { x: 28, y: 22, w: 16, h: 18 },
        shape: 3,
        label: 'Mặt Thánh Gióng (Gióng)',
      },
      {
        image: '/images/artifacts/binh-phong-nguyen-gia-tri.webp',
        width: 904,
        height: 736,
        slot: { x: 28, y: 24, w: 16, h: 18 },
        shape: 4,
        label: 'Thiếu nữ dạo xuân (Bình Phong)',
      },
    ],
    description:
      'Bức chân dung bé Thúy 8 tuổi ngồi trên ghế mây với ánh mắt trong sáng, hồn nhiên là biểu tượng đỉnh cao của nền hội họa hiện đại Việt Nam thế kỷ 20.',
  },
  {
    id: 1,
    name: 'Màn 2',
    title: 'Hai thiếu nữ và em bé',
    author: 'Tô Ngọc Vân',
    year: '1944',
    material: 'Sơn dầu',
    status: 'Bảo vật Quốc gia (2013)',
    image: '/images/artifacts/hai-thieu-nu-va-em-be-to-ngoc-van.webp',
    width: 1897,
    height: 2560,
    slots: [
      { id: 0, x: 10, y: 10, w: 18, h: 14, shape: 1, label: 'Khung cửa sổ & tán lá vườn' },
      { id: 1, x: 64, y: 8, w: 18, h: 13, shape: 0, label: 'Gương mặt thiếu nữ áo trắng' },
      { id: 2, x: 36, y: 24, w: 18, h: 13, shape: 2, label: 'Búi tóc & vai áo trắng' },
      { id: 3, x: 8, y: 28, w: 18, h: 14, shape: 3, label: 'Thiếu nữ áo vàng ngồi nghiêng' },
      { id: 4, x: 72, y: 38, w: 18, h: 14, shape: 4, label: 'Bình hoa sen phía sau' },
      { id: 5, x: 42, y: 44, w: 18, h: 14, shape: 5, label: 'Em bé thơ ngây ngồi giữa' },
      { id: 6, x: 14, y: 58, w: 18, h: 14, shape: 2, label: 'Cánh tay & vạt áo vàng' },
      { id: 7, x: 52, y: 72, w: 20, h: 15, shape: 3, label: 'Tà áo dài trắng thướt tha' },
    ],
    decoys: [
      {
        image: '/images/artifacts/em-thuy-tran-van-can.webp',
        width: 1655,
        height: 2196,
        slot: { x: 36, y: 24, w: 18, h: 13 },
        shape: 0,
        label: 'Bé Thúy (Em Thúy)',
      },
      {
        image: '/images/artifacts/binh-phong-nguyen-gia-tri.webp',
        width: 904,
        height: 736,
        slot: { x: 48, y: 46, w: 16, h: 19 },
        shape: 5,
        label: 'Tà áo hoa cẩn xà cừ (Bình Phong)',
      },
    ],
    description:
      'Kiệt tác mô tả vẻ đẹp dịu dàng, thanh lịch của phụ nữ Hà thành những năm 1940 với hòa sắc tươi sáng và bố cục tạo hình tam giác kinh điển.',
  },
  {
    id: 2,
    name: 'Màn 3',
    title: 'Bình Phong',
    author: 'Nguyễn Gia Trí',
    year: '1939 - 1944',
    material: 'Sơn mài',
    status: 'Bảo vật Quốc gia (2013)',
    image: '/images/artifacts/binh-phong-nguyen-gia-tri.webp',
    width: 904,
    height: 736,
    slots: [
      { id: 0, x: 6, y: 8, w: 16, h: 18, shape: 2, label: 'Tán lá son đỏ vườn xuân' },
      { id: 1, x: 54, y: 10, w: 16, h: 18, shape: 0, label: 'Cụm thiếu nữ dát vàng trung tâm' },
      { id: 2, x: 28, y: 24, w: 16, h: 18, shape: 4, label: 'Thiếu nữ dạo vườn bên trái' },
      { id: 3, x: 78, y: 20, w: 15, h: 18, shape: 1, label: 'Thiếu nữ bên phải ngắm hoa' },
      { id: 4, x: 8, y: 52, w: 16, h: 18, shape: 3, label: 'Khóm cây cổ thụ & rêu phong' },
      { id: 5, x: 48, y: 46, w: 16, h: 19, shape: 5, label: 'Tà áo dài cẩn vỏ trứng' },
      { id: 6, x: 26, y: 68, w: 16, h: 18, shape: 1, label: 'Thảm hoa cỏ vườn xuân' },
      { id: 7, x: 74, y: 62, w: 16, h: 18, shape: 2, label: 'Vạt áo dài sau tha thướt' },
    ],
    decoys: [
      {
        image: '/images/artifacts/bac-ho-o-chien-khu-viet-bac-duong-bich-lien.webp',
        width: 3304,
        height: 1844,
        slot: { x: 62, y: 30, w: 14, h: 20 },
        shape: 5,
        label: 'Bác Hồ (Chiến khu Việt Bắc)',
      },
      {
        image: '/images/artifacts/ket-nap-dang-o-dien-bien-phu-nguyen-sang.webp',
        width: 2560,
        height: 1617,
        slot: { x: 28, y: 22, w: 14, h: 21 },
        shape: 0,
        label: 'Chiến sĩ tuyên thệ (Điện Biên Phủ)',
      },
    ],
    description:
      'Tác phẩm sơn mài hai mặt khổ lớn với kỹ thuật mài then, cẩn vỏ trứng và dát vàng bậc thầy tạo nên cảnh thiếu nữ dạo chơi trong vườn xuân lung linh huyền ảo.',
  },
  {
    id: 3,
    name: 'Màn 4',
    title: 'Gióng',
    author: 'Nguyễn Tư Nghiêm',
    year: '1990',
    material: 'Sơn mài',
    status: 'Bảo vật Quốc gia (2017)',
    image: '/images/artifacts/giong-nguyen-tu-nghiem.webp',
    width: 2560,
    height: 1902,
    slots: [
      { id: 0, x: 8, y: 8, w: 16, h: 18, shape: 3, label: 'Vầng hào quang rực rỡ' },
      { id: 1, x: 52, y: 6, w: 16, h: 18, shape: 4, label: 'Bờm ngựa sắt phi nước đại' },
      { id: 2, x: 28, y: 22, w: 16, h: 18, shape: 0, label: 'Gương mặt cương nghị Thánh Gióng' },
      { id: 3, x: 76, y: 26, w: 16, h: 18, shape: 1, label: 'Mũi ngựa sắt & hàm cương' },
      { id: 4, x: 6, y: 44, w: 16, h: 18, shape: 2, label: 'Bụi tre ngà quật khởi' },
      { id: 5, x: 48, y: 42, w: 16, h: 18, shape: 5, label: 'Thân ngựa sắt & cơ bắp' },
      { id: 6, x: 72, y: 60, w: 16, h: 18, shape: 3, label: 'Họa tiết rồng & mây đình làng' },
      { id: 7, x: 26, y: 68, w: 18, h: 18, shape: 2, label: 'Vó ngựa sắt đạp lửa hồng' },
    ],
    decoys: [
      {
        image: '/images/artifacts/hai-thieu-nu-va-em-be-to-ngoc-van.webp',
        width: 1897,
        height: 2560,
        slot: { x: 42, y: 44, w: 18, h: 14 },
        shape: 4,
        label: 'Em bé (Hai thiếu nữ)',
      },
      {
        image: '/images/artifacts/em-thuy-tran-van-can.webp',
        width: 1655,
        height: 2196,
        slot: { x: 46, y: 44, w: 18, h: 13 },
        shape: 3,
        label: 'Cổ áo lá sen (Em Thúy)',
      },
    ],
    description:
      'Cách tân ngôn ngữ hội họa lập thể kết hợp họa tiết điêu khắc đình làng cổ truyền, thể hiện sức mạnh quật khởi của người anh hùng làng Gióng.',
  },
  {
    id: 4,
    name: 'Màn 5',
    title: 'Bác Hồ ở chiến khu Việt Bắc',
    author: 'Dương Bích Liên',
    year: '1980',
    material: 'Sơn mài',
    status: 'Bảo vật Quốc gia (2017)',
    image: '/images/artifacts/bac-ho-o-chien-khu-viet-bac-duong-bich-lien.webp',
    width: 3304,
    height: 1844,
    slots: [
      { id: 0, x: 6, y: 8, w: 14, h: 20, shape: 1, label: 'Đỉnh núi đá mây mù Việt Bắc' },
      { id: 1, x: 52, y: 6, w: 14, h: 20, shape: 2, label: 'Dãy núi rừng đại ngàn' },
      { id: 2, x: 80, y: 14, w: 13, h: 20, shape: 3, label: 'Bầu trời mây chiến khu' },
      { id: 3, x: 26, y: 24, w: 14, h: 21, shape: 4, label: 'Đầu & bờm chú ngựa nâu' },
      { id: 4, x: 62, y: 30, w: 14, h: 20, shape: 5, label: 'Gương mặt Bác Hồ ung dung' },
      { id: 5, x: 8, y: 52, w: 14, h: 21, shape: 0, label: 'Dòng suối reo chân núi' },
      { id: 6, x: 42, y: 48, w: 14, h: 21, shape: 1, label: 'Thân áo chàm giản dị của Bác' },
      { id: 7, x: 72, y: 64, w: 14, h: 20, shape: 2, label: 'Phiến đá phủ rêu phong' },
    ],
    decoys: [
      {
        image: '/images/artifacts/binh-phong-nguyen-gia-tri.webp',
        width: 904,
        height: 736,
        slot: { x: 78, y: 20, w: 15, h: 18 },
        shape: 1,
        label: 'Thiếu nữ ngắm hoa (Bình Phong)',
      },
      {
        image: '/images/artifacts/em-thuy-tran-van-can.webp',
        width: 1655,
        height: 2196,
        slot: { x: 20, y: 70, w: 19, h: 15 },
        shape: 5,
        label: 'Chân ghế mây (Em Thúy)',
      },
    ],
    description:
      'Hình tượng lãnh tụ giản dị, ung dung trên lưng ngựa vượt đèo suối đại ngàn trong kháng chiến chống Pháp với sắc nâu son mài sâu thẳm.',
  },
  {
    id: 5,
    name: 'Màn 6',
    title: 'Kết nạp Đảng ở Điện Biên Phủ',
    author: 'Nguyễn Sáng',
    year: '1963',
    material: 'Sơn mài',
    status: 'Bảo vật Quốc gia (2013)',
    image: '/images/artifacts/ket-nap-dang-o-dien-bien-phu-nguyen-sang.webp',
    width: 2560,
    height: 1617,
    slots: [
      { id: 0, x: 6, y: 8, w: 14, h: 20, shape: 2, label: 'Chiến sĩ ôm súng cảnh giới' },
      { id: 1, x: 54, y: 6, w: 14, h: 20, shape: 3, label: 'Lá cờ Tổ quốc trong chiến hào' },
      { id: 2, x: 78, y: 16, w: 14, h: 20, shape: 1, label: 'Đồng chí chỉ huy trang nghiêm' },
      { id: 3, x: 28, y: 22, w: 14, h: 21, shape: 0, label: 'Chiến sĩ mới giơ tay tuyên thệ' },
      { id: 4, x: 60, y: 34, w: 14, h: 21, shape: 4, label: 'Nụ cười đồng chí động viên' },
      { id: 5, x: 8, y: 50, w: 14, h: 21, shape: 5, label: 'Chiến sĩ băng bó đầu quả cảm' },
      { id: 6, x: 38, y: 58, w: 14, h: 21, shape: 2, label: 'Báng súng & hòm đạn' },
      { id: 7, x: 74, y: 62, w: 14, h: 21, shape: 3, label: 'Chiến hào & khói lửa Điện Biên' },
    ],
    decoys: [
      {
        image: '/images/artifacts/giong-nguyen-tu-nghiem.webp',
        width: 2560,
        height: 1902,
        slot: { x: 8, y: 8, w: 16, h: 18 },
        shape: 3,
        label: 'Vầng hào quang (Gióng)',
      },
      {
        image: '/images/artifacts/hai-thieu-nu-va-em-be-to-ngoc-van.webp',
        width: 1897,
        height: 2560,
        slot: { x: 64, y: 8, w: 18, h: 13 },
        shape: 0,
        label: 'Thiếu nữ áo trắng (Hai thiếu nữ)',
      },
    ],
    description:
      'Khoảnh khắc kết nạp Đảng thiêng liêng ngay trong chiến hào sục sôi khói lửa Điện Biên Phủ với đường nét đồ họa chắc khỏe, quả cảm.',
  },
];

// Fisher-Yates uniform shuffle
const shuffleArray = <T,>(array: T[]): T[] => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Check if any two slots overlap (with 1.5% clearance buffer)
const checkSlotsOverlap = (slots: SlotConfig[]): boolean => {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const s1 = slots[i];
      const s2 = slots[j];
      const xOverlap = !(s1.x + s1.w + 1.5 <= s2.x || s2.x + s2.w + 1.5 <= s1.x);
      const yOverlap = !(s1.y + s1.h + 1.5 <= s2.y || s2.y + s2.h + 1.5 <= s1.y);
      if (xOverlap && yOverlap) return true;
    }
  }
  return false;
};

// Generate safe randomized positions with organic jitter and varied jigsaw tab shapes
const generateRandomizedSlots = (baseSlots: SlotConfig[]): SlotConfig[] => {
  const maxAttempts = 40;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const candidate = baseSlots.map((s) => {
      // Subtle organic jitter between -1.8% and +1.8%
      const jx = (Math.random() - 0.5) * 3.6;
      const jy = (Math.random() - 0.5) * 3.6;
      const nx = Math.round(Math.max(4.0, Math.min(96.0 - s.w, s.x + jx)) * 10) / 10;
      const ny = Math.round(Math.max(4.0, Math.min(96.0 - s.h, s.y + jy)) * 10) / 10;
      const randomShape = (s.shape + attempt + Math.floor(Math.random() * 6)) % 6;
      return {
        ...s,
        x: nx,
        y: ny,
        shape: randomShape,
      };
    });

    if (!checkSlotsOverlap(candidate)) {
      return candidate;
    }
  }
  return baseSlots;
};

// High-fidelity Web Audio feedback synthesizer for gamification
const playAudioFeedback = (type: 'snap' | 'bounce' | 'victory') => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    if (type === 'snap') {
      // 1. Mechanical crisp click transient
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'triangle';
      osc1.frequency.setValueAtTime(1400, ctx.currentTime);
      osc1.frequency.exponentialRampToValueAtTime(320, ctx.currentTime + 0.04);
      gain1.gain.setValueAtTime(0.4, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start();
      osc1.stop(ctx.currentTime + 0.05);

      // 2. Resonant warm chord (snap confirmation: "khịch!")
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(523.25, ctx.currentTime + 0.02); // C5
      osc2.frequency.exponentialRampToValueAtTime(659.25, ctx.currentTime + 0.14); // E5
      gain2.gain.setValueAtTime(0.35, ctx.currentTime + 0.02);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + 0.02);
      osc2.stop(ctx.currentTime + 0.2);
    } else if (type === 'bounce') {
      // Descending wobble ("nảy về khay")
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(260, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(130, ctx.currentTime + 0.18);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    } else if (type === 'victory') {
      // Fanfare arpeggio
      const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.08);
        gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.08);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.08);
        osc.stop(ctx.currentTime + i * 0.08 + 0.36);
      });
    }
  } catch (_) {}
};

export const PuzzleGame: React.FC<PuzzleGameProps> = ({ onClose, onOpenLeaderboard }) => {
  const [selectedStageIdx, setSelectedStageIdx] = useState<number>(0);
  const currentStage = STAGES[selectedStageIdx] || STAGES[0];

  // Active slots for the current round (randomized with organic jitter and varied jigsaw tabs)
  const [activeSlots, setActiveSlots] = useState<SlotConfig[]>([]);
  const currentSlots = activeSlots.length > 0 ? activeSlots : currentStage.slots;

  // Game state
  const [timer, setTimer] = useState<number>(0);
  const [timerActive, setTimerActive] = useState<boolean>(false);
  const [moves, setMoves] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [completedStages, setCompletedStages] = useState<Set<number>>(new Set());

  // Board and Tray state
  const [placedSlots, setPlacedSlots] = useState<Record<number, PieceItem>>({});
  const [trayPieces, setTrayPieces] = useState<PieceItem[]>([]);
  const [selectedPieceId, setSelectedPieceId] = useState<string | null>(null);

  // Interaction feedback states
  const [shakingSlotIdx, setShakingSlotIdx] = useState<number | null>(null);
  const [snapSlotIdx, setSnapSlotIdx] = useState<number | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState<boolean>(false);

  // Drag & drop high-performance direct-DOM state (0 React re-renders during motion)
  const [activeDragPiece, setActiveDragPiece] = useState<PieceItem | null>(null);
  const [dragGhostDimensions, setDragGhostDimensions] = useState<{ w: number; h: number }>({ w: 60, h: 60 });
  const [hoveredSlotIdx, setHoveredSlotIdx] = useState<number | null>(null);

  const ghostRef = useRef<HTMLDivElement>(null);
  const currentHoveredSlotRef = useRef<number | null>(null);
  const dragDataRef = useRef<{
    piece: PieceItem;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    isDragging: boolean;
    targetBoardW: number;
    targetBoardH: number;
    trayW: number;
    trayH: number;
    trayScale: number;
    currentScale: number;
    boardBottom: number;
    trayTop: number;
  } | null>(null);
  const slotRectsRef = useRef<{
    idx: number;
    left: number;
    top: number;
    right: number;
    bottom: number;
    centerX: number;
    centerY: number;
  }[]>([]);

  const trayRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const boardStageRef = useRef<HTMLDivElement>(null);
  const [boardBoxSize, setBoardBoxSize] = useState<{ width: number; height: number }>({ width: 280, height: 210 });

  // Compute exact pixel size for the puzzle board to preserve artwork aspect ratio without distortion or black voids
  useEffect(() => {
    const updateSize = () => {
      const container = boardStageRef.current;
      const stageW = currentStage.width || 1000;
      const stageH = currentStage.height || 1000;
      const aspect = stageW / stageH;

      let availableW = 340;
      let availableH = 230;

      if (container) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 50 && rect.height > 50) {
          availableW = rect.width - 8;
          availableH = rect.height - 8;
        }
      } else if (typeof window !== 'undefined') {
        availableW = Math.min(window.innerWidth * 0.92, 380);
        availableH = Math.min(window.innerHeight * 0.35, 230);
      }

      let h = availableH;
      let w = h * aspect;
      if (w > availableW) {
        w = availableW;
        h = w / aspect;
      }
      setBoardBoxSize({ width: Math.round(w), height: Math.round(h) });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [currentStage]);

  // Initialize round
  const initRound = useCallback((stageIdx: number, startTimerImmediately = false) => {
    const stage = STAGES[stageIdx] || STAGES[0];
    const randomized = generateRandomizedSlots(stage.slots);
    setActiveSlots(randomized);

    setTimer(0);
    setTimerActive(startTimerImmediately);
    setMoves(0);
    setIsCompleted(false);
    setPlacedSlots({});
    setSelectedPieceId(null);
    setShakingSlotIdx(null);
    setSnapSlotIdx(null);
    setHoveredSlotIdx(null);
    setActiveDragPiece(null);
    dragDataRef.current = null;
    currentHoveredSlotRef.current = null;

    const pieces: PieceItem[] = [];

    // Correct pieces based on randomized slots
    randomized.forEach((slot, i) => {
      pieces.push({
        id: `p-${stageIdx}-${i}-${slot.x}-${slot.y}`,
        targetSlotIndex: i,
        isDecoy: false,
        image: stage.image,
        stageWidth: stage.width,
        stageHeight: stage.height,
        slot: { x: slot.x, y: slot.y, w: slot.w, h: slot.h },
        shape: slot.shape,
        label: slot.label,
      });
    });

    // Decoy pieces from other artworks
    stage.decoys.forEach((decoy, i) => {
      pieces.push({
        id: `d-${stageIdx}-${i}`,
        targetSlotIndex: -1,
        isDecoy: true,
        image: decoy.image,
        stageWidth: decoy.width,
        stageHeight: decoy.height,
        slot: { x: decoy.slot.x, y: decoy.slot.y, w: decoy.slot.w, h: decoy.slot.h },
        shape: decoy.shape,
        label: decoy.label,
      });
    });

    // Uniform Fisher-Yates shuffle
    setTrayPieces(shuffleArray(pieces));
  }, []);

  useEffect(() => {
    initRound(selectedStageIdx);
  }, [selectedStageIdx, initRound]);

  // Timer interval
  useEffect(() => {
    let interval: any = null;
    if (timerActive && !isCompleted) {
      interval = setInterval(() => setTimer((t) => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive, isCompleted]);

  const formatTime = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  // Attempt to place a piece into a slot (Core P0.7 logic)
  const handleAttemptPlace = useCallback(
    (piece: PieceItem, slotIdx: number) => {
      if (isCompleted) return;
      if (!timerActive) setTimerActive(true);
      setMoves((m) => m + 1);

      const slotConfig = currentSlots[slotIdx];
      if (!slotConfig) return;

      // Check match
      const isCorrect = !piece.isDecoy && piece.targetSlotIndex === slotIdx;

      if (isCorrect) {
        // SNAP IN PLACE! ("Khịch vào một phát")
        playAudioFeedback('snap');
        try {
          if ('vibrate' in navigator) navigator.vibrate?.([30, 20, 50]);
        } catch (_) {}

        setSnapSlotIdx(slotIdx);
        setTimeout(() => setSnapSlotIdx(null), 600);

        setPlacedSlots((prev) => {
          const updated = { ...prev, [slotIdx]: piece };

          // Check if all slots filled
          if (Object.keys(updated).length === currentSlots.length) {
            setIsCompleted(true);
            setTimerActive(false);
            setCompletedStages((prevDone) => new Set([...prevDone, selectedStageIdx]));
            playAudioFeedback('victory');
            try {
              confetti({
                particleCount: 110,
                spread: 80,
                origin: { y: 0.55 },
              });
            } catch (_) {}
          }
          return updated;
        });

        // Remove from tray
        setTrayPieces((prev) => prev.filter((p) => p.id !== piece.id));
        setSelectedPieceId(null);
      } else {
        // BOUNCE BACK TO TRAY! ("Nảy về tray, rung slot báo lỗi")
        playAudioFeedback('bounce');
        try {
          if ('vibrate' in navigator) navigator.vibrate?.([60, 40, 60]);
        } catch (_) {}

        setShakingSlotIdx(slotIdx);
        setTimeout(() => setShakingSlotIdx(null), 450);

        setSelectedPieceId(null);
      }
    },
    [isCompleted, timerActive, currentStage, selectedStageIdx]
  );

  // Return a placed piece from a slot back to the tray
  const handleReturnPieceToTray = (slotIdx: number) => {
    if (isCompleted) return;
    const piece = placedSlots[slotIdx];
    if (!piece) return;

    setPlacedSlots((prev) => {
      const updated = { ...prev };
      delete updated[slotIdx];
      return updated;
    });
    setTrayPieces((prev) => [...prev, piece]);
    setSelectedPieceId(null);
    setMoves((m) => m + 1);
  };

  // Position ghost on exact mount frame with zero jump
  useLayoutEffect(() => {
    if (activeDragPiece && ghostRef.current && dragDataRef.current) {
      const data = dragDataRef.current;
      ghostRef.current.style.transform = `translate3d(${data.currentX - data.targetBoardW / 2}px, ${data.currentY - data.targetBoardH / 2}px, 0) scale(${data.currentScale})`;
    }
  }, [activeDragPiece]);

  // High-performance, GPU-accelerated Drag & Drop Pointer Handling (Zero-latency 60/120fps)
  const handlePointerDown = (e: React.PointerEvent, piece: PieceItem) => {
    if (isCompleted || e.button !== 0) return;

    // Calculate aspect-ratio dimensions for ghost
    const pieceAspect = (piece.slot.w / piece.slot.h) * (piece.stageWidth / piece.stageHeight);

    // Kích thước chuẩn trên board (pixel) - vừa khít phần chưa ghép trên tranh
    const targetBoardW = Math.max(34, Math.round((piece.slot.w / 100) * boardBoxSize.width));
    const targetBoardH = Math.max(34, Math.round((piece.slot.h / 100) * boardBoxSize.height));

    // Kích thước thu nhỏ của mảnh khi ở trong khay (tray)
    const trayH = 46;
    const trayW = Math.max(34, Math.min(68, Math.round(trayH * pieceAspect)));

    // Tỉ lệ scale ban đầu khi ở khay so với board
    const trayScale = Math.max(0.35, Math.min(1.0, Math.min(trayW / targetBoardW, trayH / targetBoardH)));

    // Bounding positions for smooth board-to-tray transition
    const boardRect = boardRef.current?.getBoundingClientRect();
    const boardBottom = boardRect ? boardRect.bottom : window.innerHeight * 0.45;
    const trayRect = trayRef.current?.getBoundingClientRect();
    const trayTop = trayRect ? trayRect.top : window.innerHeight * 0.75;

    dragDataRef.current = {
      piece,
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      isDragging: false,
      targetBoardW,
      targetBoardH,
      trayW,
      trayH,
      trayScale,
      currentScale: trayScale,
      boardBottom,
      trayTop,
    };
    currentHoveredSlotRef.current = null;

    // Cache slot hit-boxes in viewport coordinates (avoids synchronous DOM reflow during drag)
    if (boardRef.current) {
      const slotElems = boardRef.current.querySelectorAll('[data-slot-index]');
      slotRectsRef.current = Array.from(slotElems).map((el) => {
        const r = el.getBoundingClientRect();
        return {
          idx: Number(el.getAttribute('data-slot-index')),
          left: r.left,
          top: r.top,
          right: r.right,
          bottom: r.bottom,
          centerX: r.left + r.width / 2,
          centerY: r.top + r.height / 2,
        };
      });
    }

    const onPointerMove = (moveEv: PointerEvent) => {
      const data = dragDataRef.current;
      if (!data) return;

      const dist = Math.hypot(moveEv.clientX - data.startX, moveEv.clientY - data.startY);

      if (!data.isDragging && dist > 6) {
        data.isDragging = true;
        setActiveDragPiece(data.piece);
        setDragGhostDimensions({ w: data.targetBoardW, h: data.targetBoardH });
      }

      if (data.isDragging) {
        if (moveEv.cancelable) {
          moveEv.preventDefault();
        }

        const py = moveEv.clientY;
        let scale = 1.0;

        if (py >= data.trayTop) {
          // Ở khay: kích cỡ khay
          scale = data.trayScale;
        } else if (py <= data.boardBottom + 20) {
          // ĐÃ LÊN TRANH: KÍCH CỠ VỪA KHÍT 100% VỚI PHẦN CHƯA GHÉP TRÊN TRANH!
          scale = 1.0;
        } else {
          // Chuyển động mượt mà từ khay lên tranh
          const progress = (data.trayTop - py) / (data.trayTop - (data.boardBottom + 20));
          scale = data.trayScale + Math.max(0, Math.min(1, progress)) * (1.0 - data.trayScale);
        }

        data.currentScale = scale;
        data.currentX = moveEv.clientX;
        data.currentY = moveEv.clientY;

        // DIRECT GPU TRANSFORM UPDATE (0ms latency, completely bypasses React render tree)
        if (ghostRef.current) {
          ghostRef.current.style.transform = `translate3d(${moveEv.clientX - data.targetBoardW / 2}px, ${moveEv.clientY - data.targetBoardH / 2}px, 0) scale(${scale})`;
        }

        // Fast geometric hit testing with 20px magnetic buffer
        let matchedIdx: number | null = null;
        const px = moveEv.clientX;

        for (const r of slotRectsRef.current) {
          if (
            px >= r.left - 20 &&
            px <= r.right + 20 &&
            py >= r.top - 20 &&
            py <= r.bottom + 20
          ) {
            matchedIdx = r.idx;
            break;
          }
        }

        if (matchedIdx !== currentHoveredSlotRef.current) {
          currentHoveredSlotRef.current = matchedIdx;
          setHoveredSlotIdx(matchedIdx);
        }
      }
    };

    const onPointerUp = (upEv: PointerEvent) => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      const data = dragDataRef.current;
      const matchedIdx = currentHoveredSlotRef.current;
      const wasDragging = data?.isDragging;
      const draggedPiece = data?.piece;

      dragDataRef.current = null;
      currentHoveredSlotRef.current = null;
      setActiveDragPiece(null);
      setHoveredSlotIdx(null);

      if (wasDragging && draggedPiece) {
        if (matchedIdx !== null) {
          handleAttemptPlace(draggedPiece, matchedIdx);
        } else {
          // Bounce back to tray
          playAudioFeedback('bounce');
        }
      } else if (draggedPiece) {
        // Tap / Click fallback: toggle selection
        setSelectedPieceId((prev) => (prev === draggedPiece.id ? null : draggedPiece.id));
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  const correctCount = Object.keys(placedSlots).length;
  const activeSelectedPiece = trayPieces.find((p) => p.id === selectedPieceId);

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-b from-[#190a18] via-[#240e21] to-[#12050f] text-[#fff4dd] select-none overflow-hidden relative font-sans">
      {/* AMBIENT BACKGROUND GLOW & HERITAGE MOTIFS */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#e6ba55]/30 via-transparent to-transparent"
        aria-hidden="true"
      />
      <div
        className="absolute -top-12 -left-12 w-48 h-48 rounded-full bg-[#e6ba55]/10 blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full bg-[#a50021]/15 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      {/* SVG INTERLOCKING JIGSAW CLIP-PATH DEFINITIONS (P0.7 - 6 DIVERSE SHAPES) */}
      <svg
        className="sr-only absolute w-0 h-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <defs>
          {/* Mảnh 0: Tab nhô bên phải, rãnh lõm ở đáy */}
          <clipPath id="jigsaw-clip-0" clipPathUnits="objectBoundingBox">
            <path d="M 0.08,0.08 L 0.92,0.08 L 0.920,0.395 C 0.906,0.430 0.948,0.410 0.990,0.428 C 1.000,0.500 0.990,0.572 0.948,0.590 C 0.906,0.570 0.920,0.605 0.920,0.605 L 0.92,0.92 L 0.605,0.920 C 0.570,0.934 0.590,0.892 0.572,0.850 C 0.500,0.840 0.428,0.850 0.410,0.892 C 0.430,0.934 0.395,0.920 0.395,0.920 L 0.08,0.92 L 0.08,0.08 Z" />
          </clipPath>
          {/* Mảnh 1: Rãnh lõm ở đỉnh, Tab nhô bên phải */}
          <clipPath id="jigsaw-clip-1" clipPathUnits="objectBoundingBox">
            <path d="M 0.08,0.08 L 0.395,0.080 C 0.430,0.066 0.410,0.108 0.428,0.150 C 0.500,0.161 0.572,0.150 0.590,0.108 C 0.570,0.066 0.605,0.080 0.605,0.080 L 0.92,0.08 L 0.920,0.395 C 0.906,0.430 0.948,0.410 0.990,0.428 C 1.000,0.500 0.990,0.572 0.948,0.590 C 0.906,0.570 0.920,0.605 0.920,0.605 L 0.92,0.92 L 0.08,0.92 L 0.08,0.08 Z" />
          </clipPath>
          {/* Mảnh 2: Tab nhô ở đáy, rãnh lõm bên trái */}
          <clipPath id="jigsaw-clip-2" clipPathUnits="objectBoundingBox">
            <path d="M 0.08,0.08 L 0.92,0.08 L 0.92,0.92 L 0.605,0.920 C 0.570,0.906 0.590,0.948 0.572,0.990 C 0.500,1.000 0.428,0.990 0.410,0.948 C 0.430,0.906 0.395,0.920 0.395,0.920 L 0.08,0.92 L 0.080,0.605 C 0.066,0.570 0.108,0.590 0.150,0.572 C 0.161,0.500 0.150,0.428 0.108,0.410 C 0.066,0.430 0.080,0.395 0.080,0.395 L 0.08,0.08 Z" />
          </clipPath>
          {/* Mảnh 3: Tab nhô ở đỉnh, rãnh lõm bên phải, Tab nhô bên trái */}
          <clipPath id="jigsaw-clip-3" clipPathUnits="objectBoundingBox">
            <path d="M 0.08,0.08 L 0.395,0.080 C 0.430,0.094 0.410,0.052 0.428,0.010 C 0.500,-0.001 0.572,0.010 0.590,0.052 C 0.570,0.094 0.605,0.080 0.605,0.080 L 0.92,0.08 L 0.920,0.395 C 0.934,0.430 0.892,0.410 0.850,0.428 C 0.840,0.500 0.850,0.572 0.892,0.590 C 0.934,0.570 0.920,0.605 0.920,0.605 L 0.92,0.92 L 0.08,0.92 L 0.080,0.605 C 0.094,0.570 0.052,0.590 0.010,0.572 C -0.001,0.500 0.010,0.428 0.052,0.410 C 0.094,0.430 0.080,0.395 0.080,0.395 L 0.08,0.08 Z" />
          </clipPath>
          {/* Mảnh 4: Rãnh lõm ở đỉnh, Tab nhô bên phải, rãnh lõm ở đáy */}
          <clipPath id="jigsaw-clip-4" clipPathUnits="objectBoundingBox">
            <path d="M 0.08,0.08 L 0.395,0.080 C 0.430,0.066 0.410,0.108 0.428,0.150 C 0.500,0.161 0.572,0.150 0.590,0.108 C 0.570,0.066 0.605,0.080 0.605,0.080 L 0.92,0.08 L 0.920,0.395 C 0.906,0.430 0.948,0.410 0.990,0.428 C 1.000,0.500 0.990,0.572 0.948,0.590 C 0.906,0.570 0.920,0.605 0.920,0.605 L 0.92,0.92 L 0.605,0.920 C 0.570,0.934 0.590,0.892 0.572,0.850 C 0.500,0.840 0.428,0.850 0.410,0.892 C 0.430,0.934 0.395,0.920 0.395,0.920 L 0.08,0.92 L 0.08,0.08 Z" />
          </clipPath>
          {/* Mảnh 5: Tab nhô bên phải, Tab nhô ở đáy, rãnh lõm bên trái */}
          <clipPath id="jigsaw-clip-5" clipPathUnits="objectBoundingBox">
            <path d="M 0.08,0.08 L 0.92,0.08 L 0.920,0.395 C 0.906,0.430 0.948,0.410 0.990,0.428 C 1.000,0.500 0.990,0.572 0.948,0.590 C 0.906,0.570 0.920,0.605 0.920,0.605 L 0.92,0.92 L 0.605,0.920 C 0.570,0.906 0.590,0.948 0.572,0.990 C 0.500,1.000 0.428,0.990 0.410,0.948 C 0.430,0.906 0.395,0.920 0.395,0.920 L 0.08,0.92 L 0.080,0.605 C 0.066,0.570 0.108,0.590 0.150,0.572 C 0.161,0.500 0.150,0.428 0.108,0.410 C 0.066,0.430 0.080,0.395 0.080,0.395 L 0.08,0.08 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* STAGE SELECTOR & QUICK ACTIONS STRIP */}
      <div className="px-2.5 py-1.5 bg-[#1f0d1d]/90 backdrop-blur-md border-b border-[#e6ba55]/30 flex items-center justify-between gap-1.5 shrink-0 z-20">
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-0.5">
          {STAGES.map((s, idx) => {
            const isActive = idx === selectedStageIdx;
            const isDone = completedStages.has(idx);
            return (
              <button
                key={s.id}
                onClick={() => setSelectedStageIdx(idx)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all whitespace-nowrap flex items-center gap-1 shrink-0 ${
                  isActive
                    ? 'bg-gradient-to-r from-[#d91e36] via-[#a50021] to-[#f59e0b] text-white border border-[#fef08a] shadow-[0_0_12px_rgba(245,158,11,0.5)] scale-102'
                    : isDone
                    ? 'bg-gradient-to-r from-[#047857] to-[#059669] text-[#ecfdf5] border border-[#34d399]'
                    : 'bg-[#2b1426] text-[#f5d0c5] border border-[#7a395c]/50 hover:border-[#e6ba55]/60 hover:text-white'
                }`}
              >
                <span>{isDone ? '✓' : idx + 1}</span>
                <span>{s.name}</span>
                <span className="text-[9px] opacity-75 font-normal">({s.slots.length} ô)</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setShowArtworkModal(true)}
            className="px-2.5 py-1 rounded-full bg-gradient-to-r from-[#3b192e] to-[#250e1e] border border-[#e6ba55]/70 text-[#fef08a] hover:border-[#ffd700] hover:text-white transition-all flex items-center gap-1 text-[11px] font-bold shadow-sm active:scale-95"
            title="Xem tranh tham chiếu"
          >
            <ZoomIn className="w-3.5 h-3.5" />
            <span>Tranh gốc</span>
          </button>

          <button
            onClick={onOpenLeaderboard}
            className="px-2.5 py-1 rounded-full bg-gradient-to-r from-[#3b192e] to-[#250e1e] border border-[#e6ba55]/70 text-[#fef08a] hover:border-[#ffd700] hover:text-white transition-all flex items-center gap-1 text-[11px] font-bold shadow-sm active:scale-95"
            title="Bảng xếp hạng Top 100"
          >
            <Trophy className="w-3.5 h-3.5" />
            <span>BXH</span>
          </button>
        </div>
      </div>

      {/* NON-SCROLLABLE MAIN GAME ZONE (FIT VIEWPORT) */}
      <div className="flex-1 min-h-0 flex flex-col px-2.5 py-1 sm:px-4 sm:py-1.5 max-w-lg mx-auto w-full justify-between gap-1 sm:gap-1.5 overflow-hidden z-10">
        {/* GAMIFIED HUD STATS BAR */}
        <div className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-[#251122]/90 backdrop-blur-md border border-[#e6ba55]/50 shadow-[0_4px_16px_rgba(0,0,0,0.5)] text-xs font-bold shrink-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="truncate max-w-[120px] sm:max-w-[180px] font-serif font-black text-xs sm:text-sm bg-gradient-to-r from-[#fffbeb] via-[#fde68a] to-[#f59e0b] bg-clip-text text-transparent">
              {currentStage.title}
            </span>
            <span className="text-[10px] text-[#f5d0c5]/70 hidden sm:inline">
              · {currentStage.author}
            </span>
          </div>

          <div className="flex items-center gap-1 font-mono text-[#fef08a] text-xs bg-black/50 border border-[#e6ba55]/40 px-2 py-0.5 rounded-full shadow-inner">
            <Clock className="w-3 h-3 text-[#f59e0b]" />
            <span>{formatTime(timer)}</span>
          </div>

          <div className="flex items-center gap-2">
            <div
              className={`px-2.5 py-0.5 rounded-full border text-[10px] font-black flex items-center gap-1.5 ${
                correctCount === currentSlots.length
                  ? 'bg-gradient-to-r from-[#065f46] to-[#047857] text-[#a7f3d0] border-[#34d399] shadow-[0_0_10px_rgba(16,185,129,0.4)]'
                  : 'bg-gradient-to-r from-[#451a03] to-[#78350f] text-[#fef08a] border-[#e6ba55]/70'
              }`}
            >
              <span>{correctCount} / {currentSlots.length}</span>
              <div className="flex items-center gap-0.5">
                {currentSlots.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      placedSlots[i] ? 'bg-[#34d399] shadow-[0_0_4px_#34d399]' : 'bg-[#e6ba55]/40'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1 text-[#f5d0c5]/80 text-[10px] bg-black/40 border border-white/10 px-1.5 py-0.5 rounded-full">
              <Move className="w-2.5 h-2.5 text-[#f59e0b]" />
              <span>{moves}</span>
            </div>
          </div>
        </div>

        {/* MAIN JIGSAW CANVAS (P0.7) */}
        <div ref={boardStageRef} className="flex-1 min-h-0 w-full flex items-center justify-center relative my-auto py-0.5">
          <div
            className="relative rounded-2xl overflow-hidden border-2 border-[#e6ba55] bg-[#12050e] shadow-[0_0_25px_rgba(230,186,85,0.35),0_10px_30px_rgba(0,0,0,0.8)] ring-1 ring-[#ffd700]/50 group shrink-0"
            style={{
              width: `${boardBoxSize.width}px`,
              height: `${boardBoxSize.height}px`,
            }}
          >
            {/* National Treasure Badge */}
            <div className="absolute top-1.5 left-1.5 z-25 px-2 py-0.5 rounded-full bg-black/80 backdrop-blur-sm border border-[#e6ba55]/80 text-[9px] font-black text-[#fef08a] shadow flex items-center gap-1 pointer-events-none">
              <Sparkles className="w-2.5 h-2.5 text-[#fbbf24] animate-pulse" />
              <span>BẢO VẬT QUỐC GIA</span>
            </div>

            {/* Master artwork */}
            <img
              src={currentStage.image}
              alt={currentStage.title}
              className={`absolute inset-0 w-full h-full object-fill select-none pointer-events-none transition-opacity duration-500 ${
                isCompleted ? 'opacity-100' : 'opacity-85'
              }`}
            />

            {/* MISSING SLOTS OVERLAY (P0.7) */}
            <div ref={boardRef} className="absolute inset-0 z-20 pointer-events-auto">
              {currentSlots.map((slot, idx) => {
                const placedPiece = placedSlots[idx];
                const isHovered = hoveredSlotIdx === idx;
                const isShaking = shakingSlotIdx === idx;
                const isSnapping = snapSlotIdx === idx;
                const canDropSelected = activeSelectedPiece && !placedPiece;

                return (
                  <div
                    key={slot.id}
                    data-slot-index={idx}
                    onClick={() => {
                      if (placedPiece) {
                        handleReturnPieceToTray(idx);
                      } else if (activeSelectedPiece) {
                        handleAttemptPlace(activeSelectedPiece, idx);
                      }
                    }}
                    style={{
                      left: `${slot.x}%`,
                      top: `${slot.y}%`,
                      width: `${slot.w}%`,
                      height: `${slot.h}%`,
                    }}
                    className={`absolute transition-all duration-200 cursor-pointer ${
                      placedPiece
                        ? 'z-10'
                        : isHovered || canDropSelected
                        ? 'z-25 scale-102'
                        : 'z-10'
                    } ${isShaking ? 'animate-slot-shake' : ''} ${
                      isSnapping ? 'animate-snap-flash' : ''
                    }`}
                  >
                    {/* EMPTY SLOT: JIGSAW CUTOUT RECESS (Mờ và tối hơn như yêu cầu) */}
                    {!placedPiece && (
                      <div
                        className={`w-full h-full relative transition-all duration-200 overflow-hidden ${
                          isHovered
                            ? 'scale-102 ring-2 ring-[#ffd700] shadow-[0_0_24px_rgba(255,215,0,0.85)]'
                            : canDropSelected
                            ? 'ring-2 ring-[#e6ba55] animate-pulse'
                            : isShaking
                            ? 'ring-2 ring-[#ef4444]'
                            : ''
                        }`}
                        style={{
                          clipPath: `url(#jigsaw-clip-${slot.shape})`,
                        }}
                      >
                        {/* Artwork slice underneath: gently softened and slightly dimmed (dễ nhìn, không bị tối đen) */}
                        <img
                          src={currentStage.image}
                          alt=""
                          className="absolute max-w-none pointer-events-none object-fill filter blur-[1.5px] brightness-[0.78] saturate-[0.92] contrast-[0.95]"
                          style={{
                            width: `${(100 / slot.w) * 100}%`,
                            height: `${(100 / slot.h) * 100}%`,
                            left: `${(-slot.x / slot.w) * 100}%`,
                            top: `${(-slot.y / slot.h) * 100}%`,
                          }}
                        />

                        {/* Delicate lacquer scrim & soft cutout shadow */}
                        <div
                          className={`absolute inset-0 transition-colors duration-200 ${
                            isHovered
                              ? 'bg-[#ffd700]/30 shadow-[inset_0_0_18px_rgba(255,215,0,0.7)]'
                              : canDropSelected
                              ? 'bg-[#e6ba55]/20'
                              : isShaking
                              ? 'bg-[#450a0a]/75'
                              : 'bg-black/15 shadow-[inset_0_2px_6px_rgba(0,0,0,0.45)]'
                          }`}
                        />

                        {/* Dashed jigsaw cut outline overlay */}
                        <div className="absolute inset-0 border-2 border-dashed border-[#e6ba55]/60 pointer-events-none" />

                        {/* Slot label badge */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 p-0.5">
                          <span className="text-[9px] font-black text-[#fef08a] bg-gradient-to-r from-[#7f1d1d]/90 via-[#881337]/90 to-[#4c0519]/90 px-1.5 py-0.5 rounded-full border border-[#e6ba55]/80 shadow-[0_0_8px_rgba(230,186,85,0.7)] flex items-center gap-1 pointer-events-none whitespace-nowrap">
                            <Sparkles className="w-2.5 h-2.5 text-[#fbbf24]" />
                            <span>Ô {idx + 1}</span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* FILLED SLOT: SNAPPED PIECE */}
                    {placedPiece && (
                      <div
                        className="w-full h-full relative overflow-hidden group/placed"
                        style={{
                          clipPath: `url(#jigsaw-clip-${slot.shape})`,
                        }}
                        title="Chạm để gỡ mảnh về khay"
                      >
                        <img
                          src={placedPiece.image}
                          alt=""
                          className="absolute max-w-none pointer-events-none object-fill"
                          style={{
                            width: `${(100 / placedPiece.slot.w) * 100}%`,
                            height: `${(100 / placedPiece.slot.h) * 100}%`,
                            left: `${(-placedPiece.slot.x / placedPiece.slot.w) * 100}%`,
                            top: `${(-placedPiece.slot.y / placedPiece.slot.h) * 100}%`,
                          }}
                        />
                        {/* Hover hint overlay */}
                        <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover/placed:opacity-100 text-[9px] font-bold bg-black/85 px-1.5 py-0.5 rounded text-white transition-opacity">
                            Gỡ ra
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* COMPLETED OVERLAY BANNER (GAMIFIED VICTORY) */}
            {isCompleted && (
              <div className="absolute inset-0 z-30 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center p-3 text-center animate-fade-in">
                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#f59e0b] via-[#fbbf24] to-[#fef08a] text-[#451a03] flex items-center justify-center mb-1.5 shadow-[0_0_24px_rgba(245,158,11,0.8)] animate-bounce">
                  <Trophy className="w-6 h-6" />
                </div>
                <h3 className="text-sm sm:text-base font-serif font-black bg-gradient-to-r from-[#fffbeb] via-[#fde68a] to-[#f59e0b] bg-clip-text text-transparent">
                  HOÀN THÀNH KIỆT TÁC!
                </h3>
                <div className="flex items-center gap-1 my-1 text-[#f59e0b]">
                  {[0, 1, 2].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-current drop-shadow" />
                  ))}
                </div>
                <p className="text-[11px] text-[#fff4dd] mt-0.5 max-w-xs">
                  Bạn đã ghép xuất sắc <b>{currentSlots.length}/{currentSlots.length} mảnh</b> bức tranh <b>{currentStage.title}</b> trong <b>{formatTime(timer)}</b> với <b>{moves}</b> lượt!
                </p>
                <div className="mt-2.5 flex items-center gap-2">
                  <button
                    onClick={() => {
                      const next = (selectedStageIdx + 1) % STAGES.length;
                      setSelectedStageIdx(next);
                    }}
                    className="px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#d91e36] via-[#a50021] to-[#f59e0b] text-white border border-[#fef08a] shadow-[0_0_14px_rgba(245,158,11,0.6)] text-xs font-bold flex items-center gap-1 active:scale-95"
                  >
                    <span>Màn tiếp theo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={onOpenLeaderboard}
                    className="px-3 py-1.5 rounded-full bg-[#3b192e] border border-[#e6ba55]/80 text-[#fef08a] text-xs font-bold flex items-center gap-1 hover:text-white active:scale-95"
                  >
                    <Trophy className="w-3.5 h-3.5" />
                    <span>BXH</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CANDIDATE PIECES TRAY */}
        <div className="p-2 bg-[#251122]/90 backdrop-blur-md rounded-2xl border border-[#e6ba55]/40 shadow-xl shrink-0 space-y-1">
          <div className="flex items-center justify-between text-[10px] px-1">
            <div className="flex items-center gap-1 font-serif font-black text-[#fef08a] uppercase tracking-wider">
              <Sparkles className="w-3 h-3 text-[#fbbf24]" />
              <span>Khay mảnh ghép ({trayPieces.length})</span>
            </div>
            <span className="text-[#f5d0c5]/70">
              Kéo hoặc chạm mảnh để lắp vào ô khuyết
            </span>
          </div>

          {/* TRAY PIECES LIST */}
          <div
            ref={trayRef}
            className="flex items-center gap-2.5 overflow-x-auto py-1 px-1 min-h-[58px] scrollbar-thin"
          >
            {trayPieces.length === 0 && !isCompleted && (
              <p className="text-xs text-[#f5d0c5]/80 italic py-2 w-full text-center">
                Tất cả mảnh ghép đã được đặt lên tranh!
              </p>
            )}

            {trayPieces.map((piece) => {
              const isSelected = selectedPieceId === piece.id;
              const isBeingDragged = activeDragPiece?.id === piece.id;
              const pieceAspect = (piece.slot.w / piece.slot.h) * (piece.stageWidth / piece.stageHeight);
              const trayH = 46;
              const trayW = Math.max(34, Math.min(68, Math.round(trayH * pieceAspect)));

              return (
                <div
                  key={piece.id}
                  onPointerDown={(e) => handlePointerDown(e, piece)}
                  className={`relative shrink-0 cursor-grab active:cursor-grabbing transition-all select-none touch-none ${
                    isBeingDragged
                      ? 'opacity-25 scale-95 border-2 border-dashed border-[#e6ba55]/60'
                      : isSelected
                      ? 'scale-110 shadow-[0_0_20px_rgba(255,215,0,0.95)] z-20 ring-2 ring-[#ffd700]'
                      : 'hover:scale-105 hover:shadow-lg active:scale-95'
                  }`}
                  style={{
                    height: `${trayH}px`,
                    width: `${trayW}px`,
                    filter: isBeingDragged
                      ? 'none'
                      : isSelected
                      ? 'drop-shadow(0 0 10px #ffd700)'
                      : 'drop-shadow(0 4px 8px rgba(0,0,0,0.6))',
                  }}
                  title={piece.label || 'Mảnh ghép'}
                >
                  <div
                    className="w-full h-full relative overflow-hidden"
                    style={{
                      clipPath: `url(#jigsaw-clip-${piece.shape})`,
                    }}
                  >
                    <img
                      src={piece.image}
                      alt={piece.label || 'Mảnh ghép'}
                      className="absolute max-w-none pointer-events-none object-fill"
                      style={{
                        width: `${(100 / piece.slot.w) * 100}%`,
                        height: `${(100 / piece.slot.h) * 100}%`,
                        left: `${(-piece.slot.x / piece.slot.w) * 100}%`,
                        top: `${(-piece.slot.y / piece.slot.h) * 100}%`,
                      }}
                    />
                  </div>

                  {isSelected && !isBeingDragged && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#f59e0b] text-[#231419] flex items-center justify-center text-[10px] font-black shadow-lg ring-1 ring-white">
                      ✓
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* BOTTOM ACTION BAR */}
        <div className="flex items-center gap-2 pt-0.5 pb-[max(4px,env(safe-area-inset-bottom,4px))] shrink-0">
          <button
            onClick={() => initRound(selectedStageIdx)}
            className="flex-1 py-1.5 sm:py-2 px-3 rounded-xl bg-gradient-to-b from-[#3d182f] to-[#250d1d] hover:from-[#4d1f3b] text-[#fef08a] border border-[#e6ba55]/60 hover:border-[#ffd700] text-xs font-bold flex items-center justify-center gap-1.5 shadow-md active:translate-y-0.5 active:scale-98 transition-all"
          >
            <RotateCcw className="w-3.5 h-3.5 text-[#f59e0b]" />
            <span>Xếp lại màn</span>
          </button>

          <button
            onClick={() => setShowArtworkModal(true)}
            className="flex-1 py-1.5 sm:py-2 px-3 rounded-xl bg-gradient-to-b from-[#3d182f] to-[#250d1d] hover:from-[#4d1f3b] text-[#fef08a] border border-[#e6ba55]/60 hover:border-[#ffd700] text-xs font-bold flex items-center justify-center gap-1.5 shadow-md active:translate-y-0.5 active:scale-98 transition-all"
          >
            <Info className="w-3.5 h-3.5 text-[#f59e0b]" />
            <span>Chi tiết tác phẩm</span>
          </button>
        </div>
      </div>

      {/* FLOATING DRAG GHOST: Hardware-accelerated, Zero-latency (No position transitions) */}
      {activeDragPiece && (
        <div
          ref={ghostRef}
          className="fixed top-0 left-0 pointer-events-none z-[9999] will-change-transform"
          style={{
            width: `${dragGhostDimensions.w}px`,
            height: `${dragGhostDimensions.h}px`,
            transform: 'translate3d(-9999px, -9999px, 0)',
            transformOrigin: 'center center',
          }}
        >
          {/* Inner visual container: fits slot perfectly on board with magnetic gold aura */}
          <div
            className={`w-full h-full relative overflow-hidden transition-shadow duration-150 ${
              hoveredSlotIdx !== null
                ? 'ring-2 ring-[#ffd700] drop-shadow-[0_0_24px_rgba(255,215,0,0.95)]'
                : 'drop-shadow-[0_16px_28px_rgba(0,0,0,0.75)] drop-shadow-[0_0_12px_rgba(230,186,85,0.6)]'
            }`}
            style={{
              clipPath: `url(#jigsaw-clip-${activeDragPiece.shape})`,
            }}
          >
            <img
              src={activeDragPiece.image}
              alt=""
              className="absolute max-w-none pointer-events-none object-fill"
              style={{
                width: `${(100 / activeDragPiece.slot.w) * 100}%`,
                height: `${(100 / activeDragPiece.slot.h) * 100}%`,
                left: `${(-activeDragPiece.slot.x / activeDragPiece.slot.w) * 100}%`,
                top: `${(-activeDragPiece.slot.y / activeDragPiece.slot.h) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* ARTWORK LIGHTBOX MODAL */}
      {showArtworkModal && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowArtworkModal(false)}
        >
          <div
            className="relative max-w-lg w-full bg-[#240e21] border-2 border-[#e6ba55] rounded-2xl overflow-hidden shadow-2xl space-y-3 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#e6ba55]/40 pb-2">
              <div className="flex items-center gap-2">
                <span className="text-[#f59e0b] text-base">🎨</span>
                <div>
                  <h4 className="font-serif font-black text-sm bg-gradient-to-r from-[#fffbeb] via-[#fde68a] to-[#f59e0b] bg-clip-text text-transparent">
                    {currentStage.title}
                  </h4>
                  <span className="text-[11px] text-[#f5d0c5]/80">
                    {currentStage.author} ({currentStage.year}) · {currentStage.material}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowArtworkModal(false)}
                className="w-7 h-7 rounded-full bg-[#3b192e] border border-[#e6ba55]/60 flex items-center justify-center text-[#fef08a] hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="relative w-full max-h-[50vh] rounded-xl overflow-hidden border border-[#e6ba55]/60 bg-black/50 flex items-center justify-center">
              <img
                src={currentStage.image}
                alt={currentStage.title}
                className="max-h-[50vh] w-auto object-contain"
              />
              <span className="absolute bottom-2 left-2 px-2.5 py-0.5 rounded-full bg-black/80 border border-[#e6ba55]/80 text-[10px] font-bold text-[#fef08a]">
                {currentStage.status}
              </span>
            </div>

            <p className="text-xs text-[#fff4dd] leading-relaxed">
              {currentStage.description}
            </p>

            <button
              onClick={() => setShowArtworkModal(false)}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#d91e36] via-[#a50021] to-[#f59e0b] text-white border border-[#fef08a] shadow-lg text-xs font-bold active:scale-98"
            >
              Đóng và tiếp tục ghép tranh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
